import { useState, useEffect } from "react";
import { Plus, Phone, Trash2, X } from "lucide-react";

const CRISIS_LINES = [
  { country: "España", name: "Emergencias", number: "112", type: "emergency" },
  { country: "España", name: "Atención Conducta Suicida", number: "024", type: "crisis" },
  { country: "México", name: "Emergencias", number: "911", type: "emergency" },
  { country: "México", name: "CONADIC (Drogas/Alcohol)", number: "800 911 2000", type: "crisis" },
  { country: "Argentina", name: "SEDRONAR", number: "0800 222 1002", type: "crisis" },
  { country: "Colombia", name: "Emergencias", number: "123", type: "emergency" },
  { country: "Perú", name: "CEDRO", number: "01 440 0676", type: "crisis" },
  { country: "Chile", name: "Emergencias", number: "131", type: "emergency" },
];

const RELATIONSHIPS = ["Terapeuta", "Médico", "Familiar", "Amigo/a", "Sponsor", "Grupo de apoyo", "Otro"];

function AddContactForm({ onSave, onClose }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [relationship, setRelationship] = useState("Familiar");

  const handleSave = () => {
    if (!name.trim() || !phone.trim()) return;
    const contacts = JSON.parse(localStorage.getItem("reconectados_contacts") || "[]");
    contacts.push({ id: Date.now().toString(), name, phone, relationship });
    localStorage.setItem("reconectados_contacts", JSON.stringify(contacts));
    onSave();
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E4EBE6] p-5 mb-4 animate-fade-in-up">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-[#1E2F23]" style={{ fontFamily: "Manrope" }}>Nuevo contacto</h3>
        <button data-testid="close-add-contact" onClick={onClose} className="w-8 h-8 rounded-full bg-[#F7F9F7] flex items-center justify-center">
          <X size={16} className="text-[#2C4238]" />
        </button>
      </div>
      <div className="space-y-3">
        <input
          data-testid="contact-name-input"
          type="text"
          placeholder="Nombre"
          value={name}
          onChange={e => setName(e.target.value)}
          className="w-full p-4 rounded-2xl border border-[#E4EBE6] bg-[#F7F9F7] text-sm text-[#1E2F23] focus:outline-none focus:border-[#6B9080]"
        />
        <input
          data-testid="contact-phone-input"
          type="tel"
          placeholder="Teléfono"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          className="w-full p-4 rounded-2xl border border-[#E4EBE6] bg-[#F7F9F7] text-sm text-[#1E2F23] focus:outline-none focus:border-[#6B9080]"
        />
        <select
          data-testid="contact-relationship-select"
          value={relationship}
          onChange={e => setRelationship(e.target.value)}
          className="w-full p-4 rounded-2xl border border-[#E4EBE6] bg-[#F7F9F7] text-sm text-[#1E2F23] focus:outline-none focus:border-[#6B9080]"
        >
          {RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <button
          data-testid="save-contact"
          onClick={handleSave}
          disabled={!name.trim() || !phone.trim()}
          className="w-full py-4 rounded-2xl bg-[#6B9080] text-white font-semibold text-sm disabled:opacity-50"
        >
          Guardar contacto
        </button>
      </div>
    </div>
  );
}

export default function SupportNetwork() {
  const [contacts, setContacts] = useState([]);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    setContacts(JSON.parse(localStorage.getItem("reconectados_contacts") || "[]"));
  }, []);

  const handleSave = () => {
    setContacts(JSON.parse(localStorage.getItem("reconectados_contacts") || "[]"));
    setShowForm(false);
  };

  const deleteContact = (id) => {
    const updated = contacts.filter(c => c.id !== id);
    localStorage.setItem("reconectados_contacts", JSON.stringify(updated));
    setContacts(updated);
  };

  return (
    <div className="page-container">
      <div className="animate-fade-in-up mb-5">
        <h1 className="text-3xl font-bold text-[#1E2F23]" style={{ fontFamily: "Manrope" }}>Red de Apoyo</h1>
        <p className="text-sm text-[#2C4238] mt-1">Tu red de seguridad neurológica</p>
      </div>

      <div className="animate-fade-in-up delay-100 bg-[#EEF4F1] rounded-2xl p-4 mb-5">
        <p className="text-sm text-[#1E2F23] leading-relaxed">
          La <span className="font-semibold text-[#6B9080]">conexión humana libera oxitocina</span>, el antídoto natural del cortisol. Tener contactos de apoyo guardados reduce el tiempo de reacción en crisis.
        </p>
      </div>

      {!showForm && (
        <button
          data-testid="add-contact-button"
          onClick={() => setShowForm(true)}
          className="animate-fade-in-up delay-150 w-full mb-5 py-4 rounded-2xl bg-[#6B9080] text-white font-semibold text-base flex items-center justify-center gap-2"
        >
          <Plus size={20} /> Añadir contacto
        </button>
      )}

      {showForm && <AddContactForm onSave={handleSave} onClose={() => setShowForm(false)} />}

      {/* My Contacts */}
      {contacts.length > 0 && (
        <div className="mb-6 animate-fade-in-up delay-200">
          <h2 className="text-base font-semibold text-[#1E2F23] mb-3" style={{ fontFamily: "Manrope" }}>Mis contactos</h2>
          <div className="space-y-2">
            {contacts.map((c) => (
              <div key={c.id} data-testid={`contact-${c.id}`} className="bg-white rounded-2xl p-4 border border-[#E4EBE6] flex items-center justify-between">
                <a href={`tel:${c.phone}`} className="flex items-center gap-3 flex-1">
                  <div className="w-11 h-11 rounded-full bg-[#FFF4EE] flex items-center justify-center flex-shrink-0">
                    <Phone size={18} className="text-[#C47840]" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-[#1E2F23]">{c.name}</p>
                    <p className="text-xs text-[#2C4238]">{c.relationship} · {c.phone}</p>
                  </div>
                </a>
                <button
                  data-testid={`delete-contact-${c.id}`}
                  onClick={() => deleteContact(c.id)}
                  className="w-9 h-9 rounded-full bg-[#FEF0EC] flex items-center justify-center ml-2"
                >
                  <Trash2 size={16} className="text-[#E07A5F]" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Crisis lines */}
      <div className="animate-fade-in-up delay-300">
        <h2 className="text-base font-semibold text-[#1E2F23] mb-3" style={{ fontFamily: "Manrope" }}>Líneas de crisis</h2>
        <div className="space-y-2">
          {CRISIS_LINES.map((line) => (
            <a
              key={line.number}
              href={`tel:${line.number.replace(/\s/g, '')}`}
              data-testid={`crisis-line-${line.number}`}
              className="bg-white rounded-2xl p-4 border border-[#E4EBE6] flex items-center justify-between hover:border-[#6B9080] transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${line.type === "emergency" ? "bg-[#FEF0EC]" : "bg-[#EEF4F1]"}`}>
                  <Phone size={16} className={line.type === "emergency" ? "text-[#E07A5F]" : "text-[#6B9080]"} />
                </div>
                <div>
                  <p className="font-semibold text-sm text-[#1E2F23]">{line.name}</p>
                  <p className="text-xs text-[#2C4238]">{line.country} · {line.number}</p>
                </div>
              </div>
              <span className={`text-sm font-bold ${line.type === "emergency" ? "text-[#E07A5F]" : "text-[#6B9080]"}`}>{line.number}</span>
            </a>
          ))}
        </div>
      </div>

      <div className="mt-6 p-4 bg-[#FFF9F7] rounded-2xl border border-[#F4C5B5]">
        <p className="text-xs text-[#8B5A48] leading-relaxed">
          En situación de emergencia o peligro inmediato, llama siempre al número de emergencias de tu país (112, 911, 123).
        </p>
      </div>
    </div>
  );
}
